/** @jsxImportSource @emotion/react */
import {
  getColorBackgroundSelector,
  getColorGreySelector,
  getColorPrimarySelector,
  getColorErrorSelector,
  getSpacingSelector,
  getColorTextSelector,
  paletteDividerSelector,
  modalZIndexSelector
} from '@signal/theme/selectors'
import Typography from '../../Typography'
import PureButton, { PureButtonProps } from '../../PureButton'
import { lighten, darken } from '@material-ui/core'
import { motion, MotionProps } from 'framer-motion'
import estyled from '@signal/theme/emotion'

export const UserNavMenuContainer = estyled.div`
  display: flex;
  flex-direction: row;
  width: 290px;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: background-color 0.2s ease;
  /* z-index: ${modalZIndexSelector + 1} !important; */
  z-index: 9000 !important;
  position: relative;
  height: 100%;
  padding:  ${getSpacingSelector(1.5)}px;
  box-sizing: border-box;

  background-color: ${getColorBackgroundSelector('paper')};

  &:hover {
    background-color: ${props => getColorBackgroundSelector('default')(props)};
  }
`

export const SelectedDealerText = estyled(Typography)`
  color: ${getColorPrimarySelector()} !important;
  font-weight: bold !important;
  margin: 0 !important;
`

export const EmailText = estyled<{ $isImpersonating?: boolean }>(Typography)`
  color: ${({ $isImpersonating, theme }) =>
    $isImpersonating
      ? getColorErrorSelector()(theme)
      : getColorTextSelector()(theme)} !important;
  font-size: 13px !important;
  margin: 0 !important;
`

export const UserIconWrapper = estyled.div<{
  $isImpersonating?: boolean
  $isDarkMode?: boolean
}>`
  height: 40px;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: ${({ $isImpersonating, $isDarkMode, theme }) => {
    if ($isDarkMode) {
      return $isImpersonating
        ? darken(getColorErrorSelector()(theme), 0.8)
        : darken(getColorPrimarySelector()(theme), 0.7)
    }
    return $isImpersonating
      ? lighten(getColorErrorSelector()(theme), 0.8)
      : lighten(getColorPrimarySelector()(theme), 0.9)
  }};
  color: ${({ $isImpersonating, theme }) =>
    $isImpersonating
      ? getColorErrorSelector()(theme)
      : getColorPrimarySelector()(theme)};
  margin-left: ${getSpacingSelector(2)}px;
  position: relative;
`

export const StopImpersonateButton = estyled.button`
  height: 20px;
  width: 20px;
  background-color: ${({ theme }) =>
    lighten(getColorErrorSelector()(theme), 0.5)};
  color: ${getColorErrorSelector()};
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  transition: all 0.2s ease-in-out;
  position: absolute;
  bottom: -2px;
  right: -2px;
  z-index: 1;
  &:hover {
    background-color: ${({ theme }) =>
      lighten(getColorErrorSelector()(theme), 0.5)};
    color: ${({ theme }) => darken(getColorErrorSelector()(theme), 0.2)};
    transform: scale(1.1);
  }
`

export const UserMenuWrapper = estyled.div`
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
`

export const PopoverContainer = estyled<
  { $isDarkMode?: boolean } & MotionProps
>(motion.div)`
  position: absolute;
  top: 100%;
  right: 0;
  left: 0;
  z-index: ${modalZIndexSelector} !important;
  background-color: ${getColorBackgroundSelector('paper')};
  border-radius: 25px;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  box-shadow: ${({ $isDarkMode }) =>
    $isDarkMode
      ? '0px 4px 6px rgba(0, 0, 0, 0.3)'
      : '0px 4px 6px rgba(0, 0, 0, 0.07)'};
  border: 1px solid
    ${
      paletteDividerSelector as import('@emotion/serialize').Interpolation<unknown>
    };
  overflow: hidden;
`

export const PopoverContent = estyled.div`
  padding: ${getSpacingSelector(2)}px;
`

// Recent Accounts Styles
export const RecentAccountsSection = estyled.div<{ $isDarkMode?: boolean }>`
  padding: ${getSpacingSelector(1)}px 0;
  margin: 0 -${getSpacingSelector(2)}px
    ${getSpacingSelector(1.5)}px -${getSpacingSelector(2)}px;
  padding-left: ${getSpacingSelector(2)}px;
  padding-right: ${getSpacingSelector(2)}px;
  border-bottom: 1px solid
    ${({ $isDarkMode, theme }) =>
      $isDarkMode
        ? getColorGreySelector(700)(theme)
        : getColorGreySelector(200)(theme)};
  position: relative;
  padding-bottom: ${getSpacingSelector(1.5)}px;
`

export const RecentAccountsTitle = estyled(Typography)`
  font-weight: 600 !important;
  font-size: 14px !important;
  color: ${props => getColorGreySelector(700)(props)} !important;
  margin-bottom: ${getSpacingSelector(1)}px !important;
`

export const RecentAccountsList = estyled.div<{ $isDarkMode?: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: 250px;
  max-height: 250px;
  overflow-y: auto;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ $isDarkMode, theme }) =>
      $isDarkMode
        ? getColorGreySelector(800)(theme)
        : getColorBackgroundSelector('default')(theme)};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ $isDarkMode, theme }) =>
      $isDarkMode
        ? getColorGreySelector(600)(theme)
        : getColorGreySelector(300)(theme)};
    border-radius: 2px;

    &:hover {
      background: ${({ $isDarkMode, theme }) =>
        $isDarkMode
          ? getColorGreySelector(500)(theme)
          : getColorGreySelector(400)(theme)};
    }
  }
`

// Scroll overlay components for accounts list
export const AccountsScrollOverlayBottom = estyled.div<{
  $isDarkMode?: boolean
}>`
  position: absolute;
  bottom: 60px; /* Account for All Accounts button and padding */
  left: ${getSpacingSelector(2)}px;
  right: ${getSpacingSelector(2)}px;
  height: 40px;
  background: ${({ theme }) =>
    `linear-gradient(to top, ${getColorBackgroundSelector('paper')(
      theme
    )} 0%, transparent 100%)`};
  pointer-events: none;
  z-index: 10;
`

export const RecentAccountItem = estyled<
  { $isDarkMode?: boolean } & MotionProps
>(motion.div)`
  padding: ${getSpacingSelector(0.6)}px ${getSpacingSelector(1.3)}px;
  cursor: pointer;
  border-radius: 25px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ $isDarkMode, theme }) =>
      $isDarkMode
        ? getColorGreySelector(800)(theme)
        : getColorBackgroundSelector('lightBlue')(theme)};

    span {
      color: ${getColorPrimarySelector()} !important;
    }
  }
`

// Animation variants for account items
export const accountItemVariants = {
  hidden: {
    opacity: 0,
    x: -20
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.3,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
}

export const AccountName = estyled(Typography)`
  font-size: 13px !important;
  color: ${props => getColorTextSelector()(props)} !important;
  margin: 0 !important;
`

export const DefaultMessage = estyled<{ $isDarkMode?: boolean }>(Typography)`
  font-size: 13px !important;
  color: ${({ $isDarkMode, theme }) =>
    $isDarkMode
      ? getColorGreySelector(400)(theme)
      : getColorGreySelector(500)(theme)} !important;
  font-style: italic !important;
  padding: ${getSpacingSelector(1)}px;
  text-align: center !important;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60px;
`

export const LoadingMessage = estyled<{ $isDarkMode?: boolean }>(
  DefaultMessage
)`
  &::after {
    content: '';
    width: 16px;
    height: 16px;
    margin-left: 8px;
    border: 2px solid
      ${({ $isDarkMode, theme }) =>
        $isDarkMode
          ? getColorGreySelector(600)(theme)
          : getColorGreySelector(300)(theme)};
    border-top: 2px solid ${getColorPrimarySelector()};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

export const AccountSelectorButton = estyled.a`
  display: block;
  width: 100%;
  padding: ${getSpacingSelector(1)}px ${getSpacingSelector(1.5)}px;
  background-color: ${getColorPrimarySelector()};
  color: white !important;
  text-decoration: none !important;
  text-align: center;
  font-size: 13px !important;
  font-weight: 500 !important;
  border-radius: 4px;
  margin-top: ${getSpacingSelector(1)}px;
  transition: background-color 0.2s ease;
  cursor: pointer;

  &:hover {
    background-color: ${props =>
      lighten(getColorPrimarySelector()(props), -0.1)};
    text-decoration: none !important;
  }
`

export const AllAccountsButton = estyled(PureButton)`
  width: 100%;
  height: 40px;
  font-size: 13px;
  margin-top: ${getSpacingSelector(1.5)}px;
  text-decoration: none;

  svg {
    font-size: 16px;
  }
`

export const DealerGroupSection = estyled.div<{ $isDarkMode?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${getSpacingSelector(1.5)}px ${getSpacingSelector(2)}px;
  background-color: ${({ $isDarkMode, theme }) =>
    $isDarkMode
      ? darken(getColorPrimarySelector()(theme), 0.7)
      : lighten(getColorPrimarySelector()(theme), 0.9)};
  border-radius: 15px;
  margin-bottom: ${getSpacingSelector(1)}px;
  transition: all 0.2s ease-in-out;
  cursor: pointer;
  & p,
  svg {
    color: ${({ $isDarkMode, theme }) =>
      $isDarkMode
        ? lighten(getColorPrimarySelector()(theme), 0.3)
        : getColorPrimarySelector()(theme)} !important;
  }
  &:hover {
    transform: scale(1.03);
    background-color: ${({ $isDarkMode, theme }) =>
      $isDarkMode
        ? darken(getColorPrimarySelector()(theme), 0.6)
        : lighten(getColorPrimarySelector()(theme), 0.8)};
  }
`

export const ImpersonateButton = estyled.div<{
  $disabled: boolean
  $isImpersonating: boolean
  $isDarkMode: boolean
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 35px;
  height: 35px;

  background-color: ${({ $disabled, $isImpersonating, $isDarkMode, theme }) => {
    if ($disabled) {
      return $isDarkMode
        ? getColorGreySelector(700)(theme)
        : getColorGreySelector(100)(theme)
    }

    if ($isImpersonating) {
      return $isDarkMode
        ? darken(getColorErrorSelector()(theme), 0.6)
        : lighten(getColorErrorSelector()(theme), 0.9)
    }

    // Default primary color state
    return $isDarkMode
      ? darken(getColorPrimarySelector()(theme), 0.7)
      : lighten(getColorPrimarySelector()(theme), 0.9)
  }};
  border-radius: 50%;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease-in-out;
  transform: ${({ $disabled }) => ($disabled ? 'none' : 'scale(1.05)')};

  & svg {
    fill: ${({ $disabled, theme, $isImpersonating, $isDarkMode }) => {
      if ($disabled) {
        return $isDarkMode
          ? getColorGreySelector(500)(theme)
          : getColorGreySelector(400)(theme)
      }

      if ($isImpersonating) {
        return getColorErrorSelector()(theme)
      }

      return getColorPrimarySelector()(theme)
    }};
    margin-left: 2px;
  }

  &:hover {
    background-color: ${({
      $disabled,
      $isImpersonating,
      $isDarkMode,
      theme
    }) => {
      if ($disabled) {
        return $isDarkMode
          ? getColorGreySelector(600)(theme)
          : getColorGreySelector(200)(theme)
      }

      if ($isImpersonating) {
        return $isDarkMode
          ? darken(getColorErrorSelector()(theme), 0.3)
          : lighten(getColorErrorSelector()(theme), 0.8)
      }

      // Default primary color state
      return $isDarkMode
        ? darken(getColorPrimarySelector()(theme), 0.3)
        : lighten(getColorPrimarySelector()(theme), 0.8)
    }};
  }
`

export const ImpersonationInputContainer = estyled.div`
  display: flex;
  align-items: flex-start;
  gap: ${getSpacingSelector(0.5)}px;
  margin-bottom: ${getSpacingSelector(1.5)}px;
`

export const ImpersonationInputWrapper = estyled.div`
  flex: 1;
`

export const ImpersonationHistoryContainer = estyled.div`
  margin-bottom: ${getSpacingSelector(1.5)}px;
`

export const ImpersonationHistoryLabel = estyled(Typography)`
  font-size: 12px !important;
  color: ${props => getColorGreySelector(500)(props)} !important;
  margin: 4px 0 !important;
`

export const ImpersonationHistoryList = estyled.div`
  max-height: 120px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: ${props => getColorBackgroundSelector('default')(props)};
  }

  &::-webkit-scrollbar-thumb {
    background: ${props => getColorGreySelector(300)(props)};
    border-radius: 2px;

    &:hover {
      background: ${props => getColorGreySelector(400)(props)};
    }
  }
`

export const ImpersonationHistoryDeleteButton = estyled.button`
  opacity: 0;
  transition: opacity 0.2s ease;
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  margin-left: ${getSpacingSelector(1)}px;
  border-radius: 2px;
  color: ${props => getColorGreySelector(400)(props)};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    color: ${getColorErrorSelector()};
    transform: scale(1.05);
  }
`

export const ImpersonationHistoryItem = estyled.div<{
  $isSelected?: boolean
  $isDarkMode?: boolean
}>`
  padding: ${getSpacingSelector(0.2)}px ${getSpacingSelector(1)}px;
  cursor: pointer;
  border-radius: 4px;
  margin: 2px 0;
  display: flex;
  align-items: center;
  background-color: ${({ $isSelected, $isDarkMode, theme }) => {
    if ($isSelected) {
      return $isDarkMode
        ? darken(getColorErrorSelector()(theme), 0.6)
        : lighten(getColorErrorSelector()(theme), 0.9)
    }
    return 'transparent'
  }};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ $isSelected, $isDarkMode, theme }) => {
      if ($isSelected) {
        return $isDarkMode
          ? darken(getColorErrorSelector()(theme), 0.3)
          : lighten(getColorErrorSelector()(theme), 0.8)
      }
      return $isDarkMode
        ? getColorGreySelector(800)(theme)
        : getColorBackgroundSelector('default')(theme)
    }};

    ${ImpersonationHistoryDeleteButton} {
      opacity: 1;
    }
  }
`

export const ImpersonationHistoryEmail = estyled<{
  $isSelected?: boolean
  $isDarkMode?: boolean
}>(Typography)`
  font-size: 13px !important;
  color: ${({ $isSelected, $isDarkMode, theme }) => {
    if ($isSelected) {
      return $isDarkMode
        ? lighten(getColorErrorSelector()(theme), 0.6)
        : darken(getColorErrorSelector()(theme), 0.3)
    }
    return getColorTextSelector()(theme)
  }} !important;
  font-weight: ${({ $isSelected }) =>
    $isSelected ? '500' : 'normal'} !important;
  margin: 0 !important;
  flex: 1;
`

export const FullWidthDivider = estyled.hr<{ $isDarkMode?: boolean }>`
  margin: ${getSpacingSelector(1)}px -${getSpacingSelector(2)}px;
  border: none;
  border-bottom: 1px solid
    ${({ $isDarkMode, theme }) =>
      $isDarkMode
        ? getColorGreySelector(700)(theme)
        : getColorGreySelector(200)(theme)};
  width: calc(100% + ${getSpacingSelector(4)}px);
`

export const ActionsContainer = estyled.div`
  display: flex;
  flex-direction: row;
  gap: ${getSpacingSelector(0.5)}px;
  margin-bottom: ${getSpacingSelector(1)}px;
  margin-top: ${getSpacingSelector(1.5)}px;
  height: 40px;

  > * {
    &:first-child {
      width: 40%;
    }

    &:last-child {
      width: 60%;
    }
  }
`

export const ActionButton = estyled<{ onClick?: () => void } & PureButtonProps>(
  PureButton
)`
  width: 100%;
  font-size: 13px;

  &:first-child {
    margin-right: 2px;
  }

  svg {
    margin-right: 6px;
    font-size: 16px;
  }
`

export const PrivacyLinksContainer = estyled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${getSpacingSelector(1)}px;
  margin-top: ${getSpacingSelector(0.5)}px;
  padding: 0 ${getSpacingSelector(1.5)}px;
`

export const PrivacyLink = estyled.a`
  color: ${getColorPrimarySelector()};
  text-decoration: none;
  font-size: 10px;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`
